import HIGGINS_LOGO from './higginsLogo';
import SITE2SCOPE_LOGO from './site2scopeLogo';
import { PRODUCTION_RATES as _RATES, EXT_PRODUCTION_RATE } from './areaConstants';
import { entryM2, calcProductionSummary, DEFAULT_GIRTHS } from './calcUtils';
import { ACCESS_CATEGORIES } from './AccessTags';

// ─── Brand colours — Site 2 Scope ────────────────────────────────────────
const BRAND     = [57, 69, 83];         // #394553 — dark slate
const PURPLE    = [91, 45, 130];       // #5B2D82 — brand purple
const ORANGE    = [232, 135, 27];      // #E8871B — brand orange
const AMBER_BG  = [255, 249, 235];     // warm cream for scope notes
const AMBER_BD  = [217, 119, 6];       // deep amber border
const EXCL_BG   = [254, 236, 236];     // pink tint for exclusions
const EXCL_BD   = [185, 28, 28];       // strong red border
const LIGHT_BG  = [248, 250, 252];     // near-white background
const ACCESS_BG = [235, 245, 255];     // soft blue tint for access/safety
const ACCESS_BD = [37,  99, 235];      // blue border
const DEFECT_BG = [255, 243, 224];     // peach tint for defects
const DEFECT_BD = [194, 65, 12];       // burnt orange border
const BORDER    = [226, 232, 240];     // light gray border
const DARK_TXT  = [30, 41, 59];        // near-black text
const MID_TXT   = [71, 85, 105];       // medium gray text
const PALE_TXT  = [148, 163, 184];     // muted gray text
const WHITE     = [255, 255, 255];
// Date formatting — stored as YYYY-MM-DD, display as DD/MM/YYYY
function fmtDate(d) {
  if (!d) return new Date().toLocaleDateString('en-AU');
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) { const [y,m,day]=d.split('-'); return `${day}/${m}/${y}`; }
  return d;
}

const TEAL = ORANGE; // alias for external areas

const PAGE_W = 210; const PAGE_H = 297; const MARGIN = 14; const CONTENT_W = PAGE_W - MARGIN * 2;

// EXIF-corrected image: browser auto-rotates <img>, canvas captures corrected pixels.
// Returns { data64, w, h } so callers can derive correct height from aspect ratio.
// PDF images: cap longest side at 1200px, encode at 0.60 quality.
// A full 4032×3024 iPhone photo becomes ~1200×900 → ~150KB instead of ~4MB.
const PDF_IMG_MAX_PX = 1200;
const PDF_IMG_QUALITY = 0.60;

async function fetchOrientedImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const nw = img.naturalWidth  || img.width  || 800;
        const nh = img.naturalHeight || img.height || 600;
        // Scale down so longest side ≤ PDF_IMG_MAX_PX
        const scale = Math.min(1, PDF_IMG_MAX_PX / Math.max(nw, nh));
        const w = Math.round(nw * scale);
        const h = Math.round(nh * scale);
        const canvas = document.createElement('canvas');
        canvas.width  = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve({ data64: canvas.toDataURL('image/jpeg', PDF_IMG_QUALITY), w, h });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// Fit image into a max width (mm), preserving aspect ratio. Optional maxH cap.
function fitH(photo, targetW, maxH = null) {
  if (!photo || !photo.w || !photo.h) return targetW * 0.75;
  const h = (photo.h / photo.w) * targetW;
  return maxH ? Math.min(h, maxH) : h;
}
// When maxH caps the height, back-calculate the actual width used.
function fitW(photo, targetW, maxH) {
  if (!photo || !photo.w || !photo.h) return targetW;
  const naturalH = (photo.h / photo.w) * targetW;
  if (!maxH || naturalH <= maxH) return targetW;
  return (photo.w / photo.h) * maxH;
}

// ─── Production rates — imported from areaConstants.js (single source of truth) ──
// Edit rates in src/areaConstants.js
const PRODUCTION_RATES = {
  ..._RATES,
  '_default': { rate: 20, coats: 2, girth: 1.0 }, // fallback for custom surfaces
};
const EXT_RATE = EXT_PRODUCTION_RATE;

// Calculate total hours from all project measurements using production rates.
// Returns a whole number. Used to auto-populate the duration estimate in the Scope PDF.
export function calculateTotalHours(areas) {
  if (!areas || areas.length === 0) return 0;
  let total = 0;

  areas.forEach(area => {
    if (area.type === 'internal') {
      const bySubstrate = {};
      (area.surfaceEntries || []).forEach(e => {
        const k = e.substrate; if (!k) return;
        if (!bySubstrate[k]) bySubstrate[k] = [];
        bySubstrate[k].push(e);
      });
      Object.entries(bySubstrate).forEach(([surf, entries]) => {
        const qty = entries.reduce((s, e) => s + entryM2(surf, e), 0);
        if (!qty) return;
        const cfg = PRODUCTION_RATES[surf] || PRODUCTION_RATES['_default'];
        total += (qty * cfg.girth * cfg.coats) / cfg.rate;
      });
    } else {
      (area.surfaceEntries || []).forEach(e => {
        const m2 = entryM2(e.substrate, e);
        if (m2 > 0) total += (m2 * EXT_RATE.coats) / EXT_RATE.rate;
      });
    }
  });

  return Math.round(total);
}

function r(doc, x, y, w, h, fill, stroke) {
  if (fill) { doc.setFillColor(...fill); doc.rect(x,y,w,h,stroke?'FD':'F'); }
  else if (stroke) { doc.setDrawColor(...stroke); doc.rect(x,y,w,h,'S'); }
}
function hline(doc, x, y, w, color=BORDER) { doc.setDrawColor(...color); doc.setLineWidth(0.2); doc.line(x,y,x+w,y); }
function txt(doc, str, x, y, {size=10,bold=false,color=DARK_TXT,align='left',maxW}={}) {
  doc.setFontSize(size); doc.setFont('helvetica',bold?'bold':'normal'); doc.setTextColor(...color);
  doc.text(String(str),x,y,{align,...(maxW?{maxWidth:maxW}:{})});
}

function drawLogo(doc, x, y, onDark=false) {
  // Logo ratio: 306x164px = 1.866:1
  // At height=10mm: width = 10 * 1.866 = 18.7mm
  const logoH = 10, logoW = 18.7;
  try {
    if (onDark) {
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, y + 1, logoW + 4, logoH + 2, 1.5, 1.5, 'F');
    }
    doc.addImage(HIGGINS_LOGO, 'JPEG', x + 2, y + 2, logoW, logoH);
  } catch {
    doc.setFontSize(11); doc.setFont('helvetica','bold');
    doc.setTextColor(...(onDark ? WHITE : BRAND));
    doc.text('HIGGINS COATINGS', x, y + 8);
  }
}

function addPageFrame(doc, project, pageNum) {
  // Cover page gets its own header — skip the repeating frame header on page 1
  if (pageNum !== 1) {
    r(doc, 0, 0, PAGE_W, 1, PURPLE);
    r(doc, 0, 0, PAGE_W, 17, WHITE);
    drawLogo(doc, MARGIN - 2, 2, false);
    txt(doc, 'Site 2 Scope Report', PAGE_W/2, 10, {size:7.5, color:MID_TXT, align:'center'});
    txt(doc, project.clientName||project.projectName||'', PAGE_W-MARGIN, 10, {size:7.5, color:MID_TXT, align:'right'});
    hline(doc, 0, 17, PAGE_W, BORDER);
  }
  // Footer — on all pages
  r(doc, 0, PAGE_H-8, PAGE_W, 8, WHITE);
  hline(doc, 0, PAGE_H-8, PAGE_W, BORDER);
  txt(doc, `Page ${pageNum}`, PAGE_W/2, PAGE_H-3, {size:7, color:PALE_TXT, align:'center'});
  txt(doc, new Date().toLocaleDateString('en-AU'), MARGIN, PAGE_H-3, {size:7, color:PALE_TXT});
  txt(doc, 'CONFIDENTIAL — Higgins Coatings', PAGE_W-MARGIN, PAGE_H-3, {size:7, color:PALE_TXT, align:'right'});
}

function buildCover(doc, project, coverHeroImg) {
  // ── Cover: dual logos — Higgins left, Site2Scope right ──────────────────
  const logoW = 36, logoH = 19.3;
  try { doc.addImage(HIGGINS_LOGO, 'JPEG', MARGIN, 8, logoW, logoH); } catch {}
  try { doc.addImage(SITE2SCOPE_LOGO, 'PNG', PAGE_W - MARGIN - 28, 4, 28, 21); } catch {}
  hline(doc, 0, 30, PAGE_W, BORDER);
  r(doc, 0, 30, PAGE_W, 1.2, ORANGE);

  // ── Hero image — full-width, generous height ─────────────────────────────
  let belowHero = 38;
  if (coverHeroImg) {
    const imgY = 34;
    const MAX_HERO_H = 105;
    const heroH = Math.min(fitH(coverHeroImg, PAGE_W), MAX_HERO_H);
    const heroW = fitW(coverHeroImg, PAGE_W, MAX_HERO_H);
    const heroX = (PAGE_W - heroW) / 2;
    try {
      doc.addImage(coverHeroImg.data64, 'JPEG', heroX, imgY, heroW, heroH, undefined, 'MEDIUM');
    } catch {}
    belowHero = imgY + heroH + 2;
  }

  // ── Title block ───────────────────────────────────────────────────────────
  const titleY = belowHero + 8;
  txt(doc, 'SITE 2 SCOPE REPORT', PAGE_W/2, titleY, {size:18, bold:true, color:DARK_TXT, align:'center'});

  // Date
  txt(doc, fmtDate(project.date), PAGE_W/2, titleY + 10, {size:8.5, color:MID_TXT, align:'center'});

  // ── Project details — clean two-col table, no box ─────────────────────────
  const detailY = titleY + 18;
  hline(doc, MARGIN, detailY, CONTENT_W, BORDER);
  const ROWS = [
    ['Project',   project.projectName || project.clientName],
    ['Client',    project.clientName],
    ['Address',   project.address],
    ['Job No.',   project.jobNumber],
    ['Estimator', project.estimatorName],
    ['Date',      fmtDate(project.date)],
  ].filter(([,v]) => v);
  ROWS.forEach(([label, value], i) => {
    const ry = detailY + 7 + i * 9;
    txt(doc, label, MARGIN, ry, {size:8.5, color:PALE_TXT});
    txt(doc, value, MARGIN + 36, ry, {size:8.5, bold:true, color:DARK_TXT, maxW:CONTENT_W - 38});
    hline(doc, MARGIN, ry + 3, CONTENT_W, [240, 244, 248]);
  });
  const afterDetail = detailY + 10 + ROWS.length * 9;

  // ── Cover note ────────────────────────────────────────────────────────────
  let nextY = afterDetail + 4;
  if (project.coverNote) {
    const noteLines = doc.splitTextToSize(project.coverNote, CONTENT_W - 8);
    const noteH = noteLines.length * 5.5 + 10;
    r(doc, MARGIN, nextY, 2, noteH, ORANGE);
    noteLines.forEach((line, i) => txt(doc, line, MARGIN + 6, nextY + 7 + i * 5.5, {size:9.5, color:MID_TXT}));
    nextY += noteH + 4;
  }

  // ── Additional Comments ───────────────────────────────────────────────────
  if (project.bdmComments && project.bdmComments.trim()) {
    const commentLines = doc.splitTextToSize(project.bdmComments.trim(), CONTENT_W - 8);
    const commentH = commentLines.length * 5.5 + 14;
    r(doc, MARGIN, nextY, CONTENT_W, commentH, [248, 250, 252]);
    r(doc, MARGIN, nextY, 2, commentH, [91, 45, 130]);
    txt(doc, 'ADDITIONAL COMMENTS', MARGIN + 6, nextY + 5.5, {size:7.5, bold:true, color:[91, 45, 130]});
    commentLines.forEach((line, i) => txt(doc, line, MARGIN + 6, nextY + 11 + i * 5.5, {size:9.5, color:MID_TXT}));
    nextY += commentH + 4;
  }

  // ── Exclusions summary — collated from all areas ──────────────────────────
  const allExclusions = (project.areas || [])
    .filter(a => a.scopeExclusions && a.scopeExclusions.trim())
    .map(a => ({ name: a.name || 'Unnamed', text: a.scopeExclusions.trim() }));

  if (allExclusions.length > 0) {
    hline(doc, MARGIN, nextY, CONTENT_W, BORDER);
    nextY += 5;
    r(doc, MARGIN, nextY, CONTENT_W, 8, EXCL_BG);
    r(doc, MARGIN, nextY, 3, 8, EXCL_BD);
    txt(doc, 'EXCLUDED FROM SCOPE', MARGIN + 7, nextY + 5.5, {size:8, bold:true, color:[192,57,43]});
    nextY += 10;
    allExclusions.forEach(({name, text}) => {
      const lines = doc.splitTextToSize(text, CONTENT_W - 30);
      const rowH = lines.length * 5 + 6;
      if (nextY + rowH > PAGE_H - 35) return; // don't overflow into stats
      txt(doc, name, MARGIN + 4, nextY + 4.5, {size:7, bold:true, color:[192,57,43]});
      lines.forEach((line, i) => txt(doc, line, MARGIN + 55, nextY + 4.5 + i * 5, {size:7.5, color:[80,20,20]}));
      hline(doc, MARGIN, nextY + rowH, CONTENT_W, [235,215,215]);
      nextY += rowH;
    });
    nextY += 3;
  }

  // ── Production Summary — uses selected hours source ──────────────────────
  const areas = project.areas || [];
  const hoursSource = project.hoursSource || 'measured';
  const ps = calcProductionSummary(areas);
  const washRate = project.washRate || 75;
  const washHrs = ps.external.m2 > 0 ? ps.external.m2 / washRate : 0;

  if (hoursSource !== 'none' && nextY + 50 < PAGE_H - 15) {
    // Column visibility flags
    const cColM2  = project.showM2 !== false;
    const cColHrs = project.showHours !== false;
    const cColLit = project.showLitres !== false;

    // Compute display hours based on hoursSource
    let dIntH=ps.internal.hours, dIntDt=ps.internal.downtime, dExtH=ps.external.hours, dExtDt=ps.external.downtime;
    if (hoursSource !== 'measured') {
      dIntH=0; dIntDt=0; dExtH=0; dExtDt=0;
      areas.forEach(area => {
        const dt=(area.downtime??10)/100; let hrs=0;
        if (hoursSource==='approved' && parseFloat(area.approvedHours)>0) hrs=parseFloat(area.approvedHours);
        else if (hoursSource==='assessed' && area.assessBySubstrate) hrs=Object.values(area.assessBySubstrate).reduce((s,v)=>s+(parseFloat(v.hours)||0),0);
        if (area.type==='external'){dExtH+=hrs;dExtDt+=hrs*dt;} else {dIntH+=hrs;dIntDt+=hrs*dt;}
      });
    }
    const dTotalH=dIntH+dExtH+dIntDt+dExtDt+washHrs;
    const srcLabel=hoursSource==='approved'?' (Estimated)':hoursSource==='assessed'?' (Assessed)':'';

    hline(doc, MARGIN, nextY, CONTENT_W, BORDER);
    nextY += 4;
    txt(doc, `PROJECT SUMMARY${srcLabel}`, MARGIN, nextY + 5, {size:9, bold:true, color:PURPLE});
    nextY += 10;

    // Dynamic column positions for cover summary
    const cCols = [];
    if (cColHrs) cCols.push('hours');
    if (cColLit) cCols.push('litres');
    if (cColM2)  cCols.push('m2');
    const cColPos = {};
    cCols.forEach((col, i) => { cColPos[col] = MARGIN + 42 + i * 54; });
    const cColHdr = { hours: 'Hours', litres: 'Litres', m2: 'm²' };

    const hdrY = nextY;
    txt(doc, '', MARGIN, hdrY, {size:7, bold:true, color:PALE_TXT});
    cCols.forEach(col => { txt(doc, cColHdr[col], cColPos[col], hdrY, {size:7, bold:true, color:PALE_TXT}); });
    hline(doc, MARGIN, hdrY+3, CONTENT_W, BORDER); nextY = hdrY + 7;

    const drawRow = (label, hours, litres, m2, bold) => {
      const opts = bold ? {size:8.5, bold:true, color:PURPLE} : {size:8.5, color:DARK_TXT};
      txt(doc, label, MARGIN, nextY, opts);
      if (cColHrs && cColPos.hours)  txt(doc, hours.toFixed(1),  cColPos.hours,  nextY, opts);
      if (cColLit && cColPos.litres) txt(doc, litres.toFixed(0), cColPos.litres, nextY, opts);
      if (cColM2 && cColPos.m2)      txt(doc, m2.toFixed(0),     cColPos.m2,     nextY, opts);
      nextY += 6;
    };
    if (ps.internal.m2 > 0) drawRow('Internal', dIntH+dIntDt, ps.internal.litres, ps.internal.m2, false);
    if (ps.external.m2 > 0) drawRow('External', dExtH+dExtDt, ps.external.litres, ps.external.m2, false);
    if (washHrs > 0) {
      txt(doc, 'Ext. Wash', MARGIN, nextY, {size:8.5, color:DARK_TXT});
      if (cColHrs && cColPos.hours) txt(doc, washHrs.toFixed(1), cColPos.hours, nextY, {size:8.5, color:DARK_TXT});
      nextY += 6;
    }
    hline(doc, MARGIN, nextY-2, CONTENT_W, PURPLE); nextY += 2;
    drawRow('Total', dTotalH, ps.totalLitres, ps.internal.m2 + ps.external.m2, true);
  }
}

function buildSummaryPage(doc, project) {
  const areas=project.areas||[];
  const hoursSource=project.hoursSource||'measured';
  const ps=calcProductionSummary(areas);
  const washRate=project.washRate||75;
  const washHrs=ps.external.m2>0?ps.external.m2/washRate:0;

  const DEFAULT_SPREAD=12;
  const RATE_DEFAULTS={'Floor':18,'Ceilings':18,'Ceilings Detailed':15,'Walls':18,'Walls Detailed':15,'Windows':15,'Doors':6,'Door frames':6,'Skirting':18,'Stairs':12,'Gutter':18,'Fascia':18,'Eaves':15,'Soffit':15,'Walls - Flat':18,'Walls Rough':10,'Downpipes':16,'Balustrades/Handrails':6,'Balustrade / Handrails':6,'Slab Edge':10,'Posts':16,'Roofs':16};
  

  // Calc per-area breakdown — respects hoursSource setting
  function calcAreaProduction(area){
    // Measured hours (always calculated — used as fallback)
    const gc={};(area.repeatGroups||[]).forEach(g=>{gc[g.id]=g.count||1;});
    let measHrs=0,measLit=0;
    const byS={};
    (area.surfaceEntries||[]).forEach(e=>{const k=e.substrate;if(k){if(!byS[k])byS[k]=[];byS[k].push(e);}});
    Object.entries(byS).forEach(([sub,entries])=>{
      const s=area.surfaceSettings?.[sub]||{};
      const base=s.baseSurface||sub;
      const rate=s.paintingSpeed??RATE_DEFAULTS[base]??20;
      const girth=s.girth??DEFAULT_GIRTHS[base]??1;
      const coats=s.coats??(base==='Doors'||base==='Door frames'?3:2);
      const prep=s.prepRate??75;
      const methodPrep=s.methodPrep??0;
      entries.forEach(e=>{
        const rc=e.repeatGroupId?(gc[e.repeatGroupId]||1):1;
        const qty=entryM2(sub,e)*rc*girth;
        const paintH=qty*coats/rate;
        const prepH=prep>0?qty/prep:0;
        const methodPrepH=(paintH+prepH)*methodPrep;
        measHrs+=paintH+prepH+methodPrepH;
        measLit+=qty*coats/DEFAULT_SPREAD;
      });
    });
    const dt=(area.downtime??10)/100;

    // m² — always from measurements (not overridden by assessed/approved)
    let measM2 = 0;
    Object.entries(byS).forEach(([sub, entries]) => {
      entries.forEach(e => {
        const rc = e.repeatGroupId ? (gc[e.repeatGroupId]||1) : 1;
        measM2 += entryM2(sub, e) * rc;
      });
    });

    if (hoursSource === 'approved' && parseFloat(area.approvedHours) > 0) {
      const hrs = parseFloat(area.approvedHours);
      return {hrs, lit:measLit, m2:measM2, dt:hrs*dt, total:hrs+hrs*dt};
    }
    if (hoursSource === 'assessed' && area.assessBySubstrate) {
      const assessHrs = Object.values(area.assessBySubstrate).reduce((s,v) => s + (parseFloat(v.hours)||0), 0);
      if (assessHrs > 0) return {hrs:assessHrs, lit:measLit, m2:measM2, dt:assessHrs*dt, total:assessHrs+assessHrs*dt};
    }
    return {hrs:measHrs, lit:measLit, m2:measM2, dt:measHrs*dt, total:measHrs+measHrs*dt};
  }

  let y=22;
  r(doc,MARGIN,y,CONTENT_W,10,LIGHT_BG,BORDER);
  r(doc,MARGIN,y,3,10,PURPLE);
  const sourceLabel = hoursSource === 'approved' ? '(Estimated Hours)' : hoursSource === 'assessed' ? '(Assessed Hours)' : '(Measured Hours)';
  txt(doc,`PROJECT SUMMARY ${sourceLabel}`,MARGIN+7,y+7,{size:9.5,bold:true,color:DARK_TXT});
  y+=14;

  const internal=areas.filter(a=>a.type==='internal');
  const external=areas.filter(a=>a.type==='external');

  // Column visibility flags
  const sColM2  = project.showM2 !== false;
  const sColHrs = project.showHours !== false;
  const sColLit = project.showLitres !== false;

  // Dynamic column positions for summary table
  const sCols = [];
  if (sColM2)  sCols.push('m2');
  if (sColHrs) sCols.push('hours');
  if (sColLit) sCols.push('litres');
  const sColPos = {};
  sCols.forEach((col, i) => {
    sColPos[col] = MARGIN + 80 + (i + 1) * ((CONTENT_W - 80) / (sCols.length || 1));
  });
  const sColHdr = { m2: 'm²', hours: 'Hours', litres: 'Litres' };

  const drawTable=(title,color,areaList)=>{
    if(!areaList.length)return;
    if(y+20>PAGE_H-15){doc.addPage();addPageFrame(doc,project,2);y=22;}
    txt(doc,title,MARGIN,y,{size:9,bold:true,color}); y+=6;
    r(doc,MARGIN,y,CONTENT_W,7,LIGHT_BG);
    txt(doc,'Area',MARGIN+2,y+5,{size:8,bold:true,color:MID_TXT});
    sCols.forEach(col => {
      txt(doc, sColHdr[col], sColPos[col], y+5, {size:8,bold:true,color:MID_TXT,align:'right'});
    });
    hline(doc,MARGIN,y+7,CONTENT_W,BORDER); y+=7;

    let secTotal=0,secLit=0,secM2=0;
    areaList.forEach((area,i)=>{
      if(y+9>PAGE_H-15){doc.addPage();addPageFrame(doc,project,2);y=22;}
      const p=calcAreaProduction(area);
      if(i%2===0)r(doc,MARGIN,y,CONTENT_W,8,LIGHT_BG);
      txt(doc,area.name||'Unnamed',MARGIN+2,y+5.5,{size:8,color:DARK_TXT,maxW:70});
      if (sColM2 && sColPos.m2)     txt(doc,(p.m2||0).toFixed(0), sColPos.m2,     y+5.5,{size:8,color:DARK_TXT,align:'right'});
      if (sColHrs && sColPos.hours)  txt(doc,p.total.toFixed(1),   sColPos.hours,  y+5.5,{size:8,bold:true,color,align:'right'});
      if (sColLit && sColPos.litres) txt(doc,p.lit.toFixed(0),     sColPos.litres, y+5.5,{size:8,color:DARK_TXT,align:'right'});
      hline(doc,MARGIN,y+8,CONTENT_W,BORDER); y+=8;
      secTotal+=p.total; secLit+=p.lit; secM2+=(p.m2||0);
    });
    // Section total
    r(doc,MARGIN,y,CONTENT_W,8,color);
    txt(doc,`${title} Total`,MARGIN+2,y+5.5,{size:8.5,bold:true,color:WHITE});
    if (sColM2 && sColPos.m2)     txt(doc,secM2.toFixed(0),     sColPos.m2,     y+5.5,{size:8.5,bold:true,color:WHITE,align:'right'});
    if (sColHrs && sColPos.hours)  txt(doc,secTotal.toFixed(1),  sColPos.hours,  y+5.5,{size:8.5,bold:true,color:WHITE,align:'right'});
    if (sColLit && sColPos.litres) txt(doc,secLit.toFixed(0),    sColPos.litres, y+5.5,{size:8.5,bold:true,color:WHITE,align:'right'});
    y+=12;
  };

  drawTable('Internal',PURPLE,internal);
  drawTable('External',ORANGE,external);

  // Wash hours
  if(washHrs>0){
    if(y+10>PAGE_H-15){doc.addPage();addPageFrame(doc,project,2);y=22;}
    txt(doc,`Estimated External Wash: ${washHrs.toFixed(1)} hrs`,MARGIN+2,y+5,{size:8.5,bold:true,color:TEAL});
    txt(doc,`(${ps.external.m2.toFixed(0)} m² @ ${washRate} m²/hr)`,MARGIN+85,y+5,{size:7.5,color:MID_TXT});
    y+=10;
  }

  // Grand total — uses hoursSource
  if(y+12>PAGE_H-15){doc.addPage();addPageFrame(doc,project,2);y=22;}
  let grandTotalHrs = ps.grandTotalHours + washHrs;
  let grandTotalM2 = ps.internal.m2 + ps.external.m2;
  if (hoursSource !== 'measured') {
    grandTotalHrs = 0;
    areas.forEach(area => {
      const p = calcAreaProduction(area);
      grandTotalHrs += p.total;
    });
    grandTotalHrs += washHrs;
  }
  hline(doc,MARGIN,y,CONTENT_W,PURPLE); y+=4;
  txt(doc,'GRAND TOTAL',MARGIN+2,y+5,{size:10,bold:true,color:PURPLE});
  if (sColM2 && sColPos.m2)     txt(doc,`${grandTotalM2.toFixed(0)} m²`, sColPos.m2,     y+5,{size:10,bold:true,color:PURPLE,align:'right'});
  if (sColHrs && sColPos.hours)  txt(doc,`${grandTotalHrs.toFixed(1)} hrs`, sColPos.hours, y+5,{size:10,bold:true,color:PURPLE,align:'right'});
  if (sColLit && sColPos.litres) txt(doc,`${ps.totalLitres.toFixed(0)} L`,  sColPos.litres,y+5,{size:10,bold:true,color:PURPLE,align:'right'});

  if(!areas.length){
    txt(doc,'No areas recorded.',MARGIN,y+10,{size:9,color:PALE_TXT});
  }
}

function drawAreaSection(doc, area, heroImg, areaPhotos, yStart, project, pageCounter) {
  let y=yStart;
  const isInternal=area.type==='internal';
  const titleColor=isInternal?PURPLE:ORANGE;
  const hoursSource=project.hoursSource||'measured';

  // Title bar
  r(doc,MARGIN,y,CONTENT_W,11,titleColor);
  txt(doc,isInternal?'INTERNAL':'EXTERNAL',MARGIN+3,y+8,{size:7,bold:true,color:[180,210,240]});
  txt(doc,area.name||'Unnamed Area',MARGIN+26,y+8,{size:10,bold:true,color:WHITE});
  if(area.tag)txt(doc,`${area.tag}`,PAGE_W-MARGIN-3,y+8,{size:8,color:[200,220,240],align:'right'});
  y+=13;

  const hasHero = !!heroImg;
  const LEFT_W  = hasHero ? CONTENT_W * 0.57 : CONTENT_W;
  const RIGHT_W = CONTENT_W * 0.41;
  const RIGHT_X = MARGIN + CONTENT_W - RIGHT_W;
  const MAX_HERO_H = 70; // max mm — portrait shots won't overflow the page

  // Hero photo — width fixed to RIGHT_W, height derived from actual aspect ratio
  let heroActualH = 0;
  if (hasHero) {
    try {
      heroActualH = fitH(heroImg, RIGHT_W, MAX_HERO_H);
      const heroActualW = fitW(heroImg, RIGHT_W, MAX_HERO_H);
      // Centre horizontally if width was reduced due to maxH
      const heroX = RIGHT_X + (RIGHT_W - heroActualW) / 2;
      doc.addImage(heroImg.data64, 'JPEG', heroX, y, heroActualW, heroActualH, undefined, 'MEDIUM');
      doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
      doc.rect(heroX, y, heroActualW, heroActualH, 'S');
      if (heroImg.direction) {
        txt(doc, `Direction: ${heroImg.direction}`, heroX, y + heroActualH + 3, {size:6.5, color:MID_TXT});
      }
    } catch {}
  }

  // Production estimate — hours, litres & m² per surface (painter-focused)
  const measureY=y;
  const DEFAULT_SPREAD=12;
  const RATE_DEFAULTS={'Floor':18,'Ceilings':18,'Ceilings Detailed':15,'Walls':18,'Walls Detailed':15,'Windows':15,'Doors':6,'Door frames':6,'Skirting':18,'Stairs':12,'Gutter':18,'Fascia':18,'Eaves':15,'Soffit':15,'Walls - Flat':18,'Walls Rough':10,'Downpipes':16,'Balustrades/Handrails':6,'Balustrade / Handrails':6,'Slab Edge':10,'Posts':16,'Roofs':16};

  // Column visibility flags
  const colM2 = project.showM2 !== false;
  const colHrs = project.showHours !== false;
  const colLit = project.showLitres !== false;
  const showDetail = project.showSurfaceDetail !== false;

  const gc={};
  const groupMap={};
  (area.repeatGroups||[]).forEach(g=>{gc[g.id]=g.count||1; groupMap[g.id]=g;});
  const bySubstrate={};
  (area.surfaceEntries||[]).forEach(e=>{
    const k=e.substrate||'—';
    if(!bySubstrate[k])bySubstrate[k]=[];
    bySubstrate[k].push(e);
  });

  // Calculate per-entry production values and build grouped/ungrouped structure
  function calcEntryProd(sub, e, rc) {
    const settings=area.surfaceSettings?.[sub]||{};
    const base=settings.baseSurface||sub;
    const rate=settings.paintingSpeed??RATE_DEFAULTS[base]??20;
    const girth=settings.girth??DEFAULT_GIRTHS[base]??1;
    const coats=settings.coats??(base==='Doors'||base==='Door frames'?3:2);
    const prepRate=settings.prepRate??75;
    const methodPrep=settings.methodPrep??0;
    const m2=entryM2(sub,e)*rc;
    const qty=m2*girth;
    const paintHrs=qty*coats/rate;
    const prepHrs=prepRate>0?qty/prepRate:0;
    const methodPrepHrs=(paintHrs+prepHrs)*methodPrep;
    return {m2, hours:paintHrs+prepHrs+methodPrepHrs, litres:qty*coats/DEFAULT_SPREAD};
  }

  // Aggregate by substrate, split into ungrouped vs per-group
  // ungroupedBySub: { substrate: {name, m2, hours, litres} }
  const ungroupedBySub = {};
  // groupedData: { groupId: { group, subs: { substrate: {name, m2, hours, litres} } } }
  const groupedData = {};
  let areaTotalHrs=0, areaTotalLit=0, areaTotalM2=0;

  Object.entries(bySubstrate).forEach(([sub,entries])=>{
    const settings=area.surfaceSettings?.[sub]||{};
    const method=settings.method||'brush_roll';
    const methodLabel=method!=='brush_roll'?` (${method})`:'';
    const displayName = sub + methodLabel;

    entries.forEach(e=>{
      const gid = e.repeatGroupId;
      const rc = gid ? (gc[gid]||1) : 1;
      const prod = calcEntryProd(sub, e, rc);

      areaTotalHrs += prod.hours;
      areaTotalLit += prod.litres;
      areaTotalM2 += prod.m2;

      if (gid && groupMap[gid]) {
        if (!groupedData[gid]) groupedData[gid] = { group: groupMap[gid], subs: {} };
        const gs = groupedData[gid].subs;
        if (!gs[sub]) gs[sub] = {name:displayName, m2:0, hours:0, litres:0};
        gs[sub].m2 += prod.m2;
        gs[sub].hours += prod.hours;
        gs[sub].litres += prod.litres;
      } else {
        if (!ungroupedBySub[sub]) ungroupedBySub[sub] = {name:displayName, m2:0, hours:0, litres:0};
        ungroupedBySub[sub].m2 += prod.m2;
        ungroupedBySub[sub].hours += prod.hours;
        ungroupedBySub[sub].litres += prod.litres;
      }
    });
  });

  const ungroupedRows = Object.values(ungroupedBySub).filter(r=>r.hours>0||r.litres>0||r.m2>0);
  const groupIds = Object.keys(groupedData).filter(gid => {
    const subs = Object.values(groupedData[gid].subs);
    return subs.some(s => s.hours>0||s.litres>0||s.m2>0);
  });

  // Downtime — baked into total hours (not shown separately)
  const dtPct=(area.downtime??10)/100;
  const dtHrs=areaTotalHrs*dtPct;

  // Override total when using assessed or approved hours
  let displayTotalHrs = areaTotalHrs + dtHrs;
  if (hoursSource === 'approved' && parseFloat(area.approvedHours) > 0) {
    displayTotalHrs = parseFloat(area.approvedHours);
  } else if (hoursSource === 'assessed' && area.assessBySubstrate) {
    const ah = Object.values(area.assessBySubstrate).reduce((s,v) => s + (parseFloat(v.hours)||0), 0);
    if (ah > 0) displayTotalHrs = ah;
  }

  const hasSurfData = (ungroupedRows.length > 0 || groupIds.length > 0);

  if(hasSurfData && hoursSource !== 'none'){
    const accentColor=isInternal?PURPLE:ORANGE;

    // Determine active columns and their positions
    const activeCols = [];
    if (colM2)  activeCols.push('m2');
    if (colHrs) activeCols.push('hours');
    if (colLit) activeCols.push('litres');
    const colCount = activeCols.length;
    // Column right-edges as fraction of LEFT_W (reserve first ~45% for surface name)
    const colPositions = {};
    activeCols.forEach((col, i) => {
      colPositions[col] = MARGIN + LEFT_W * (0.5 + (i + 1) * (0.5 / (colCount || 1))) - 2;
    });
    const colHeaders = { m2: 'm²', hours: 'Hours', litres: 'Litres' };

    // Helper: draw a surface data row
    const drawDataRow = (label, m2, hours, litres, rowY, opts = {}) => {
      const { bold, color: rowColor, bg, indent } = opts;
      const textColor = rowColor || DARK_TXT;
      const nameX = MARGIN + 4 + (indent || 0);
      if (bg) r(doc, MARGIN, rowY, LEFT_W, 7, bg);
      txt(doc, label, nameX, rowY + 5, {size:8, bold:!!bold, color:textColor, maxW: (colPositions[activeCols[0]] || MARGIN+LEFT_W*0.5) - nameX - 4});
      if (colM2 && colPositions.m2)     txt(doc, m2.toFixed(0),      colPositions.m2,     rowY+5, {size:8, bold:!!bold, color:textColor, align:'right'});
      if (colHrs && colPositions.hours)  txt(doc, hours.toFixed(1),   colPositions.hours,  rowY+5, {size:8, bold:true,  color: bold ? (rowColor||accentColor) : accentColor, align:'right'});
      if (colLit && colPositions.litres) txt(doc, litres.toFixed(0),  colPositions.litres, rowY+5, {size:8, bold:!!bold, color:textColor, align:'right'});
    };

    if (showDetail && colCount > 0) {
      // Table header
      r(doc,MARGIN,y,LEFT_W,7,LIGHT_BG);
      txt(doc,'Surface',MARGIN+4,y+5,{size:8,bold:true,color:MID_TXT});
      activeCols.forEach(col => {
        txt(doc, colHeaders[col], colPositions[col], y+5, {size:8,bold:true,color:MID_TXT,align:'right'});
      });
      hline(doc,MARGIN,y+7,LEFT_W,BORDER); y+=7;

      let rowIdx = 0;

      // Ungrouped surfaces
      if (ungroupedRows.length > 0 && groupIds.length > 0) {
        // Show subtle "Ungrouped surfaces" header only when groups exist
        r(doc, MARGIN, y, LEFT_W, 6, LIGHT_BG);
        txt(doc, '── Ungrouped surfaces ──', MARGIN + 4, y + 4.5, {size:7, color:PALE_TXT});
        y += 6;
      }
      ungroupedRows.forEach(row => {
        if (y + 7 > PAGE_H - 10) {
          doc.addPage(); pageCounter.pg++; addPageFrame(doc, project, pageCounter.pg); y = 22;
        }
        if(rowIdx%2===0) r(doc,MARGIN,y,LEFT_W,7,[252,252,255]);
        drawDataRow(row.name, row.m2, row.hours, row.litres, y);
        hline(doc,MARGIN,y+7,LEFT_W,[230,230,235]); y+=7; rowIdx++;
      });

      // Repeat groups
      groupIds.forEach(gid => {
        const gd = groupedData[gid];
        const g = gd.group;
        const groupSubs = Object.values(gd.subs).filter(s => s.hours>0||s.litres>0||s.m2>0);
        if (groupSubs.length === 0) return;

        // Check page break — try to keep group header + at least 2 rows together
        const groupEstH = 6 + groupSubs.length * 7 + 7; // header + rows + subtotal
        const FOOTER_TOP = PAGE_H - 10;
        if (y + Math.min(groupEstH, 28) > FOOTER_TOP) {
          doc.addPage(); pageCounter.pg++; addPageFrame(doc, project, pageCounter.pg); y = 22;
        }

        // Group header
        r(doc, MARGIN, y, LEFT_W, 6, LIGHT_BG);
        txt(doc, `── ${g.name || 'Group'} ×${g.count || 1} ──`, MARGIN + 4, y + 4.5, {size:7, bold:true, color:MID_TXT});
        y += 6; rowIdx = 0;

        let gTotalM2=0, gTotalHrs=0, gTotalLit=0;
        groupSubs.forEach(row => {
          // Page break protection for individual rows within a group
          if (y + 7 > PAGE_H - 10) {
            doc.addPage(); pageCounter.pg++; addPageFrame(doc, project, pageCounter.pg); y = 22;
          }
          if(rowIdx%2===0) r(doc,MARGIN,y,LEFT_W,7,[252,252,255]);
          drawDataRow(row.name, row.m2, row.hours, row.litres, y, {indent:2});
          hline(doc,MARGIN,y+7,LEFT_W,[230,230,235]); y+=7; rowIdx++;
          gTotalM2 += row.m2; gTotalHrs += row.hours; gTotalLit += row.litres;
        });

        // Group subtotal
        drawDataRow(`${g.name || 'Group'} subtotal`, gTotalM2, gTotalHrs, gTotalLit, y, {bold:true, color:MID_TXT, bg:LIGHT_BG});
        hline(doc,MARGIN,y+7,LEFT_W,BORDER); y+=7;
      });
    }

    // Area total bar — always shown when there's data (even if showDetail is off)
    r(doc,MARGIN,y,LEFT_W,8,accentColor);
    txt(doc,'Area Total',MARGIN+4,y+6,{size:8.5,bold:true,color:WHITE});
    if (colM2 && colPositions.m2)     txt(doc, `${areaTotalM2.toFixed(0)} m²`,  colPositions.m2,     y+6, {size:8.5,bold:true,color:WHITE,align:'right'});
    if (colHrs && colPositions.hours)  txt(doc, `${displayTotalHrs.toFixed(1)} hrs`, colPositions.hours,  y+6, {size:8.5,bold:true,color:WHITE,align:'right'});
    if (colLit && colPositions.litres) txt(doc, `${areaTotalLit.toFixed(0)} L`,      colPositions.litres, y+6, {size:8.5,bold:true,color:WHITE,align:'right'});
    y+=10;
  }

  // Ensure y clears hero photo
  if (hasHero) y = Math.max(y, measureY + heroActualH + 4);

  // Scope notes — with page break protection
  if(area.scopeNotes){
    const noteLines=doc.splitTextToSize(area.scopeNotes,CONTENT_W-14);
    const noteH=noteLines.length*5.5+10;
    const FOOTER_TOP=PAGE_H-10;
    if(y+noteH>FOOTER_TOP){ doc.addPage(); pageCounter.pg++; addPageFrame(doc,project,pageCounter.pg); y=22; }
    r(doc,MARGIN,y,CONTENT_W,noteH,AMBER_BG,AMBER_BD);
    txt(doc,'Scope Notes',MARGIN+4,y+7,{size:8,bold:true,color:[146,64,14]});
    noteLines.forEach((line,i)=>txt(doc,line,MARGIN+4,y+13+i*5.5,{size:8,color:[120,53,15]}));
    y+=noteH+4;
  }

  // Exclusions — with page break protection
  if(area.scopeExclusions){
    const exclLines=doc.splitTextToSize(area.scopeExclusions,CONTENT_W-14);
    const exclH=exclLines.length*5.5+10;
    const FOOTER_TOP=PAGE_H-10;
    if(y+exclH>FOOTER_TOP){ doc.addPage(); pageCounter.pg++; addPageFrame(doc,project,pageCounter.pg); y=22; }
    r(doc,MARGIN,y,CONTENT_W,exclH,EXCL_BG,EXCL_BD);
    r(doc,MARGIN,y,3,exclH,EXCL_BD);
    txt(doc,'EXCLUDED FROM SCOPE',MARGIN+7,y+7,{size:8,bold:true,color:[192,57,43]});
    exclLines.forEach((line,i)=>txt(doc,line,MARGIN+7,y+13+i*5.5,{size:8,color:[140,30,20]}));
    y+=exclH+4;
  }

  // Defects / remediation notes — per area
  if(area.defectsNote && area.defectsNote.trim()){
    const defLines=doc.splitTextToSize(area.defectsNote,CONTENT_W-14);
    const defH=defLines.length*5.5+10;
    const FOOTER_TOP=PAGE_H-10;
    if(y+defH>FOOTER_TOP){ doc.addPage(); pageCounter.pg++; addPageFrame(doc,project,pageCounter.pg); y=22; }
    r(doc,MARGIN,y,CONTENT_W,defH,DEFECT_BG);
    r(doc,MARGIN,y,3,defH,DEFECT_BD);
    txt(doc,'DEFECTS / REMEDIATION',MARGIN+7,y+7,{size:8,bold:true,color:DEFECT_BD});
    defLines.forEach((line,i)=>txt(doc,line,MARGIN+7,y+13+i*5.5,{size:8,color:[120,40,5]}));
    y+=defH+4;
  }

  // Washdown hours — external areas only
  const washdownHours = area.type === 'external' && parseFloat(area.washdownHours) > 0 ? parseFloat(area.washdownHours) : 0;
  if (washdownHours > 0) {
    const FOOTER_TOP = PAGE_H - 10;
    const blockH = 14;
    if (y + blockH > FOOTER_TOP) { doc.addPage(); pageCounter.pg++; addPageFrame(doc, project, pageCounter.pg); y = 22; }
    r(doc, MARGIN, y, CONTENT_W, blockH, [236, 252, 255], [6, 182, 212]);
    r(doc, MARGIN, y, 3, blockH, [6, 182, 212]);
    txt(doc, 'WASHDOWN', MARGIN + 7, y + 5.5, { size: 7.5, bold: true, color: [14, 116, 144] });
    txt(doc, `${washdownHours.toFixed(1)} hrs — pressure wash / surface preparation`, MARGIN + 40, y + 5.5, { size: 8, color: [21, 94, 117] });
    txt(doc, `${washdownHours.toFixed(1)}h`, MARGIN + CONTENT_W - 4, y + 5.5, { size: 8, bold: true, color: [14, 116, 144], align: 'right' });
    y += blockH + 4;
  }

  // Access & safety needs — categorised with descriptions
  const accessNeeds = Array.isArray(area.accessNeeds) ? area.accessNeeds : [];
  if (accessNeeds.length > 0) {
    const FOOTER_TOP = PAGE_H - 10;
    // Resolve IDs to labels and group by category
    const allOpts = ACCESS_CATEGORIES.flatMap(c => c.options.map(o => ({...o, category: c.label})));
    const otherLabels = area.accessOtherLabels || {};
    const grouped = {};

    accessNeeds.forEach(id => {
      // Standard option — direct lookup
      const stdOpt = allOpts.find(o => o.id === id);
      if (stdOpt) {
        if (!grouped[stdOpt.category]) grouped[stdOpt.category] = [];
        grouped[stdOpt.category].push(stdOpt.label);
        return;
      }
      // Dynamic "other" — find category by prefix
      if (id.startsWith('other_')) {
        const cat = ACCESS_CATEGORIES.find(c => c.otherBase && id.startsWith(c.otherBase));
        if (cat) {
          if (!grouped[cat.label]) grouped[cat.label] = [];
          grouped[cat.label].push(otherLabels[id] || `Other (${cat.label})`);
        }
      }
    });

    // Category context sentences — use custom if provided, else defaults
    const customCtx = project.accessContexts || {};
    const defaultCtx = {
      'Internal Access': 'The following internal access requirements have been identified for this area:',
      'Internal Conditions': 'The following internal conditions apply to this area:',
      'External Access & Height': 'The following access and working-at-height requirements have been identified for this area:',
      'Site Hazards': 'The following site hazards have been identified and must be managed:',
      'Building & Condition': 'The following building condition issues have been noted:',
      'Site Conditions': 'The following site conditions apply to this area:',
    };

    // Pre-calculate block height — context sentences may be multi-line
    const catEntries = Object.entries(grouped);
    let blockH = 12; // header
    catEntries.forEach(([cat, labels]) => {
      const ctx = customCtx[cat] || defaultCtx[cat] || 'The following items apply:';
      const ctxLines = doc.splitTextToSize(ctx, CONTENT_W - 18);
      blockH += 5; // category title
      blockH += ctxLines.length * 4 + 2; // context lines
      blockH += labels.length * 4.5 + 3; // bullet items + gap
    });

    if (y + blockH > FOOTER_TOP) { doc.addPage(); pageCounter.pg++; addPageFrame(doc, project, pageCounter.pg); y = 22; }
    r(doc, MARGIN, y, CONTENT_W, blockH, ACCESS_BG, ACCESS_BD);
    r(doc, MARGIN, y, 3, blockH, ACCESS_BD);
    txt(doc, 'ACCESS, SAFETY & BUILDING REQUIREMENTS', MARGIN + 7, y + 7, { size: 7.5, bold: true, color: [37, 99, 235] });
    let tagY = y + 12;

    catEntries.forEach(([cat, labels]) => {
      // Category header
      txt(doc, cat, MARGIN + 7, tagY, { size: 7.5, bold: true, color: [30, 64, 175] });
      tagY += 4;
      // Context sentence(s) — may be multi-line if user edited
      const ctx = customCtx[cat] || defaultCtx[cat] || 'The following items apply:';
      const ctxLines = doc.splitTextToSize(ctx, CONTENT_W - 18);
      ctxLines.forEach(line => {
        txt(doc, line, MARGIN + 7, tagY, { size: 7, color: [71, 85, 105] });
        tagY += 4;
      });
      tagY += 1;
      // List items
      labels.forEach(label => {
        txt(doc, '\u2022  ' + label, MARGIN + 10, tagY, { size: 7.5, color: [30, 64, 175] });
        tagY += 4.5;
      });
      tagY += 2;
    });
    y += blockH + 4;
  }

  // Additional photos — 2-column grid with page-break protection
  const additionalPhotos = areaPhotos.slice(1).filter(p => p);
  if (additionalPhotos.length > 0) {
    const FOOTER_TOP   = PAGE_H - 10;
    const CONTENT_TOP_PG = 22;
    const GAP   = 8;  // enough room for direction label below photos
    const COL_W = (CONTENT_W - GAP) / 2;
    const MAX_H = 80;

    // Ensure the "Photos (n)" label + at least one row fits before starting
    if (y + 8 + MAX_H > FOOTER_TOP) {
      doc.addPage(); pageCounter.pg++;
      addPageFrame(doc, project, pageCounter.pg);
      y = CONTENT_TOP_PG;
    }
    txt(doc, `Photos (${areaPhotos.filter(p=>p).length})`, MARGIN, y+5, {size:8, bold:true, color:MID_TXT});
    y += 8;

    // Group into pairs (rows)
    const rows = [];
    for (let i = 0; i < additionalPhotos.length; i += 2) {
      rows.push(additionalPhotos.slice(i, i + 2));
    }

    let rowY = y;
    rows.forEach(row => {
      const rowH = Math.max(...row.map(p => fitH(p, COL_W, MAX_H)));
      // Page break before this row if it would overflow
      if (rowY + rowH > FOOTER_TOP) {
        doc.addPage(); pageCounter.pg++;
        addPageFrame(doc, project, pageCounter.pg);
        rowY = CONTENT_TOP_PG;
      }
      row.forEach((photo, ci) => {
        const cellH = fitH(photo, COL_W, MAX_H);
        const cellW = fitW(photo, COL_W, MAX_H);
        const x = ci === 0 ? MARGIN : MARGIN + COL_W + GAP;
        try {
          doc.addImage(photo.data64, 'JPEG', x, rowY, cellW, cellH, undefined, 'FAST');
          doc.setDrawColor(...BORDER); doc.setLineWidth(0.2);
          doc.rect(x, rowY, cellW, cellH, 'S');
          if (photo.direction) {
            txt(doc, photo.direction, x + 1, rowY + cellH + 3, {size:6, bold:true, color:MID_TXT});
          }
          if (photo.addedBy === 'pm') {
            r(doc, x, rowY, 18, 5, [217, 119, 6]);
            txt(doc, 'PM PHOTO', x + 1, rowY + 3.5, {size:4.5, bold:true, color:[255, 255, 255]});
          }
        } catch {}
      });
      rowY += rowH + GAP;
    });
    y = rowY;
  }

  return y+6;
}

export async function exportScopeReport(project, onProgress) {
  const areas=project.areas||[];
  onProgress?.('Fetching photos (correcting orientation)...');

  const photosPerArea={};
  for(const area of areas){
    photosPerArea[area.firestoreId || area.id]=[];
    for(const photoRef of (area.photos||[])){
      const photoData = await fetchOrientedImage(photoRef.url);
      // Preserve metadata from the original photo ref
      if(photoData) { photoData.isDefect = photoRef.isDefect; photoData.defectNote = photoRef.defectNote; photoData.isProjectHero = photoRef.isProjectHero; photoData.direction = photoRef.direction || null; }
      photosPerArea[area.firestoreId || area.id].push(photoData);
    }
  }

  onProgress?.('Building document...');

  // ── Century Gothic font (uncomment after following src/fonts/README.md) ──────
  // import CenturyGothicNormal from './fonts/CenturyGothic-normal';
  // import CenturyGothicBold   from './fonts/CenturyGothic-bold';
  // doc.addFileToVFS('CenturyGothic-normal.ttf', CenturyGothicNormal);
  // doc.addFont('CenturyGothic-normal.ttf', 'CenturyGothic', 'normal');
  // doc.addFileToVFS('CenturyGothic-bold.ttf', CenturyGothicBold);
  // doc.addFont('CenturyGothic-bold.ttf', 'CenturyGothic', 'bold');
  // Then set PDF_FONT = 'CenturyGothic' at the top of this file.
  const internal=areas.filter(a=>a.type==='internal');
  const external=areas.filter(a=>a.type==='external');
  const ordered=[...internal,...external];

  // Find the project cover hero photo across all areas
  let coverHeroImg = null;
  for (const area of areas) {
    const heroRef = (area.photos || []).find(p => p.isProjectHero);
    if (heroRef) {
      coverHeroImg = await fetchOrientedImage(heroRef.url);
      break;
    }
  }
  const { jsPDF } = await import('jspdf');
  const doc=new jsPDF({orientation:'portrait',unit:'mm',format:'a4'});

  // Cover
  buildCover(doc, project, coverHeroImg);
  addPageFrame(doc,project,1);

  // Summary — skip when hours excluded
  const hoursSource = project.hoursSource || 'measured';
  const includeHours = hoursSource !== 'none';
  let pg = 1;
  if (includeHours) {
    doc.addPage();
    pg = 2;
    addPageFrame(doc,project,pg);
    buildSummaryPage(doc,project);
  }

  // Areas — pack multiple areas per page, only break when near bottom
  const CONTENT_TOP = 20;   // y after header
  const CONTENT_BOT = PAGE_H - 10; // y before footer
  const MIN_AREA_H  = 45;   // minimum space needed to start a new area
  const pageCounter = { pg };

  let y = CONTENT_BOT; // force a new page for first area
  let prevType = null;

  for (const area of ordered) {
    // If type changes, add a section divider — needs ~14mm
    const typeChanged = area.type !== prevType;
    const spaceNeeded = MIN_AREA_H + (typeChanged ? 14 : 0);

    if (y + spaceNeeded > CONTENT_BOT) {
      doc.addPage(); pg++; pageCounter.pg = pg;
      addPageFrame(doc, project, pg);
      y = CONTENT_TOP;
      prevType = null; // re-draw type header on new page if still same type
    }

    if (area.type !== prevType) {
      // Force new page when switching between internal and external
      if (prevType !== null) {
        doc.addPage(); pg++; pageCounter.pg = pg;
        addPageFrame(doc, project, pg);
        y = CONTENT_TOP;
      }
      const bc = area.type === 'internal' ? PURPLE : ORANGE;
      const bl = includeHours
        ? (area.type === 'internal' ? 'PROJECT AREA ESTIMATES — INTERNAL' : 'PROJECT AREA ESTIMATES — EXTERNAL')
        : (area.type === 'internal' ? 'SCOPE OF WORKS — INTERNAL AREAS' : 'SCOPE OF WORKS — EXTERNAL AREAS');
      r(doc, MARGIN, y, CONTENT_W, 8, bc);
      txt(doc, bl, MARGIN+4, y+6, {size:9, bold:true, color:WHITE});
      y += 11; prevType = area.type;
    }

    const areaPhotos = photosPerArea[area.firestoreId || area.id] || [];
    const loadedPhotos = areaPhotos.filter(p => p && p.data64);
    const heroPhoto = loadedPhotos[0] || null;
    y = drawAreaSection(doc, area, heroPhoto, loadedPhotos, y, project, pageCounter);
    y += 4; // gap between areas
  }

  // ── Defects & Remediation — collated from all areas ─────────────────────
  const defectAreas = areas.filter(a =>
    (a.photos||[]).some(p => p.isDefect) || (a.defectsNote && a.defectsNote.trim())
  );

  if (defectAreas.length > 0) {
    doc.addPage(); pg++; pageCounter.pg = pg;
    addPageFrame(doc, project, pg);

    let dy = 22;
    // Section header
    r(doc, MARGIN, dy, CONTENT_W, 10, DEFECT_BG);
    r(doc, MARGIN, dy, 3, 10, DEFECT_BD);
    txt(doc, 'DEFECTS & REMEDIATION', MARGIN + 7, dy + 7, {size:9.5, bold:true, color:DEFECT_BD});
    dy += 14;

    for (const area of defectAreas) {
      const defectPhotos = (photosPerArea[area.firestoreId || area.id] || []).filter(p => p && p.isDefect);
      const hasPhotos = defectPhotos.length > 0;
      const hasNote = area.defectsNote && area.defectsNote.trim();

      // Area sub-header
      const areaColor = area.type === 'internal' ? PURPLE : ORANGE;
      r(doc, MARGIN, dy, CONTENT_W, 8, areaColor);
      txt(doc, area.name || 'Unnamed Area', MARGIN + 4, dy + 5.5, {size:8, bold:true, color:WHITE});
      if(area.tag) txt(doc, area.tag, PAGE_W-MARGIN-3, dy+5.5, {size:7, color:[220,200,220], align:'right'});
      dy += 10;

      // Defect note
      if (hasNote) {
        const noteLines = doc.splitTextToSize(area.defectsNote, CONTENT_W - 10);
        const noteH = noteLines.length * 5.2 + 8;
        r(doc, MARGIN, dy, CONTENT_W, noteH, DEFECT_BG);
        for (let i = 0; i < noteLines.length; i++) {
          txt(doc, noteLines[i], MARGIN + 4, dy + 6 + i * 5.2, {size:8, color:[120,40,5]});
        }
        dy += noteH + 4;
      }

      // Defect photos in 2-col grid
      if (hasPhotos) {
        const GAP = 4; const COL_W = (CONTENT_W - GAP) / 2; const MAX_H = 75;
        const FOOTER_TOP = PAGE_H - 10; const CONTENT_TOP = 22;
        const rows = [];
        for (let i = 0; i < defectPhotos.length; i += 2) rows.push(defectPhotos.slice(i, i+2));

        for (const row of rows) {
          const rowH = Math.max(...row.map(p => fitH(p, COL_W, MAX_H)));
          if (dy + rowH > FOOTER_TOP) {
            doc.addPage(); pg++; pageCounter.pg = pg;
            addPageFrame(doc, project, pg);
            dy = CONTENT_TOP;
          }
          for (let ci = 0; ci < row.length; ci++) {
            const photo = row[ci];
            const cellH = fitH(photo, COL_W, MAX_H);
            const cellW = fitW(photo, COL_W, MAX_H);
            const x = ci === 0 ? MARGIN : MARGIN + COL_W + GAP;
            try {
              doc.addImage(photo.data64, 'JPEG', x, dy, cellW, cellH, undefined, 'FAST');
              doc.setDrawColor(...DEFECT_BD); doc.setLineWidth(0.5);
              doc.rect(x, dy, cellW, cellH, 'S');
            } catch {}
          }
          dy += rowH + GAP;
        }
      }
      dy += 6;
    }
  }

  onProgress?.('Saving PDF...');
  const safePart=(s)=>(s||'').replace(/[^a-zA-Z0-9\s]/g,'').trim().replace(/\s+/g,'_');
  const rawDate = project.date || new Date().toISOString().slice(0,10);
  const fileDateParts = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const fileDate = fileDateParts ? `${fileDateParts[3]}-${fileDateParts[2]}-${fileDateParts[1]}` : rawDate;
  const pdfName=[safePart(project.jobNumber),safePart(project.clientName||project.projectName),fileDate].filter(Boolean).join('_');
  doc.save(`${pdfName}.pdf`);
}
